import React, { useCallback, useEffect, useState } from "react";
import { Stack, IconButton, Typography, Divider, Box, Button, CircularProgress, Checkbox, Table, TableBody, TableCell, TableContainer, TableRow, Avatar, TablePagination } from "@mui/material";
import { ArrowBack, Person, DoneAll } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import { getData, postData } from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/endpoints";
import Loader from "../../common/spinner";

function NotificationsManager() {
    const navigate = useNavigate();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);

    const [pagination, setPagination] = useState({
        page: 0,
        limit: 25,
    });

    const fetchNotificationsList = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.fetchUserNotifications(userId));

            if (response.status === "SUCCESS") {
                setNotifications(response.message || []);
            }
        } catch (error) {
            return;
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchNotificationsList();
    }, [fetchNotificationsList]);

    const handleSelectAll = () => {
        if (selectedIds.length === notifications.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(notifications.map(n => n.id));
        }
    };

    const handleSelectRow = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    // NotificationsManager.js

    const handleMarkAsRead = async () => {
        if (selectedIds.length === 0) return;
        setIsUpdating(true);
        try {
            const response = await postData(API_ENDPOINTS.markNotificationsAsRead, {
                notification_ids: selectedIds
            });
            if (response.status === "SUCCESS") {
                await fetchNotificationsList();
                setSelectedIds([]);
                window.dispatchEvent(new Event("notificationsUpdated"));
            }
        } catch (error) {
            return;
        } finally {
            setIsUpdating(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleChangeRowsPerPage = (event) => {
        setPagination(prev => ({
            ...prev,
            limit: parseInt(event.target.value, 25),
            page: 0
        }));
    };

    const paginatedNotifications = notifications.slice(
        pagination.page * pagination.limit,
        pagination.page * pagination.limit + pagination.limit
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {loading && <Loader />}

            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #e2e8f0', px: 2, py: 1 }}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ color: '#64748b', borderRadius: '4px', p: 1, '&:hover': { bgcolor: '#f1f5f9' } }}
                >
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>Back</Typography>
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>Notifications</Typography>

                    <Stack direction="row" spacing={1.5}>
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleSelectAll}
                            sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}
                        >
                            {selectedIds.length === notifications.length && notifications.length > 0 ? "UNSELECT ALL" : "SELECT ALL"}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={selectedIds.length === 0 || isUpdating}
                            startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <DoneAll />}
                            onClick={handleMarkAsRead}
                            sx={{
                                bgcolor: '#e2e8f0',
                                color: '#475569',
                                boxShadow: 'none',
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                '&:hover': { bgcolor: '#cbd5e1' },
                                '&.Mui-disabled': { bgcolor: '#f1f5f9' }
                            }}
                        >
                            Mark Read
                        </Button>
                    </Stack>
                </Box>
            </Stack>
            <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <Table stickyHeader>
                    <TableBody>
                        {paginatedNotifications.map((notif) => {
                            const isSelected = selectedIds.includes(notif.id);
                            return (
                                <TableRow
                                    key={notif.id}
                                    hover
                                    onClick={() => handleSelectRow(notif.id)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: notif.is_read ? 'transparent' : '#dde2e8',
                                        '&:last-child td, &:last-child th': { border: 0 }
                                    }}
                                >
                                    <TableCell padding="checkbox" sx={{ width: 50 }}>
                                        <Checkbox
                                            checked={isSelected}
                                            size="small"
                                            sx={{ color: '#cbd5e1' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: 60, pr: 0 }}>
                                        <Avatar sx={{ bgcolor: '#cbd5e1', width: 40, height: 40 }}>
                                            <Person />
                                        </Avatar>
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        {/* Header Row: Sender and Date */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: notif.is_read ? 'text.secondary' : 'primary.main',
                                                    fontWeight: notif.is_read ? 600 : 800,
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.7rem',
                                                    letterSpacing: 0.5
                                                }}
                                            >
                                                {notif.sender_type || "System Update"}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.disabled', fontWeight: 500 }}
                                            >
                                                {new Date(notif.createdAt).toLocaleString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </Typography>
                                        </Box>

                                        {/* Main Message Body */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#1e293b',
                                                fontWeight: notif.is_read ? 400 : 600,
                                                lineHeight: 1.5,
                                                mb: 1
                                            }}
                                        >
                                            {notif.message}
                                        </Typography>

                                        {/* Footer: Notification Category/Type */}
                                        {notif.type && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: 'inline-block',
                                                    bgcolor: notif.is_read ? '#f1f5f9' : 'rgba(25, 118, 210, 0.08)',
                                                    color: notif.is_read ? '#64748b' : 'primary.main',
                                                    px: 1,
                                                    py: 0.2,
                                                    borderRadius: 1,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 700,
                                                    border: '1px solid',
                                                    borderColor: notif.is_read ? '#e2e8f0' : 'rgba(25, 118, 210, 0.2)'
                                                }}
                                            >
                                                #{notif.type}
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!loading && notifications.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 10 }}>
                                    <Typography color="textSecondary">No notifications found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={notifications.length}
                page={pagination.page}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                sx={{ borderTop: '1px solid #e2e8f0' }}
            />
        </Box>
    );
}

export default NotificationsManager;